/**
 * Files manager hooks (platform admin)
 * Model settings and per-tenant run/token usage.
 */

import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  FilesManagerSettings,
  FilesManagerRun,
  FilesManagerRunConnection,
  UpdateFilesManagerSettingsInput,
} from '../types/admin';
import {
  GET_FILES_MANAGER_SETTINGS,
  UPDATE_FILES_MANAGER_SETTINGS,
  LIST_FILES_MANAGER_RUNS,
} from '../graphql/admin';

const isPreviewMode = () =>
  import.meta.env.VITE_PREVIEW === 'true' || !import.meta.env.VITE_COGNITO_USER_POOL_ID;

const PREVIEW_SETTINGS: FilesManagerSettings = {
  visionModelId: 'gemini-flash',
  extractionModelId: 'claude-sonnet',
  availableModels: [
    { id: 'claude-sonnet', label: 'Claude Sonnet', provider: 'bedrock', supportsVision: true },
    { id: 'gemini-flash', label: 'Gemini Flash', provider: 'google', supportsVision: true },
    { id: 'text-only', label: 'Text model', provider: 'bedrock', supportsVision: false },
  ],
  updatedAt: null,
  updatedBy: null,
};

/**
 * Load and update the Files manager model settings.
 * Errors are friendly strings; raw API messages are only logged.
 */
export function useFilesManagerSettings() {
  const [data, setData] = useState<FilesManagerSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (isPreviewMode()) {
        setData(PREVIEW_SETTINGS);
        return;
      }

      const client = generateClient();
      const response = (await client.graphql({
        query: GET_FILES_MANAGER_SETTINGS,
      })) as any;
      setData(response.data?.getFilesManagerSettings || null);
    } catch (err) {
      console.error('Failed to load Files manager settings:', err);
      setError("We couldn't load the Files manager settings. Refresh the page to try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const saveSettings = useCallback(
    async (input: UpdateFilesManagerSettingsInput): Promise<FilesManagerSettings | null> => {
      try {
        setSaving(true);
        setSaveError(null);

        if (isPreviewMode()) {
          const next = { ...PREVIEW_SETTINGS, ...input, updatedAt: new Date().toISOString(), updatedBy: 'preview' };
          setData(next);
          return next;
        }

        const client = generateClient();
        const response = (await client.graphql({
          query: UPDATE_FILES_MANAGER_SETTINGS,
          variables: { input },
        })) as any;
        const updated: FilesManagerSettings | null =
          response.data?.updateFilesManagerSettings || null;
        if (!updated) {
          throw new Error('Empty response');
        }
        setData(updated);
        return updated;
      } catch (err) {
        console.error('Failed to save Files manager settings:', err);
        setSaveError("We couldn't save the models. Try again.");
        return null;
      } finally {
        setSaving(false);
      }
    },
    []
  );

  return { data, loading, error, saving, saveError, fetchSettings, saveSettings };
}

/**
 * List Files manager runs for a tenant with "load more" pagination.
 */
export function useFilesManagerRuns(tenantId: string | null, pageSize: number = 25) {
  const [runs, setRuns] = useState<FilesManagerRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextToken, setNextToken] = useState<string | null>(null);

  const fetchRuns = useCallback(
    async (token?: string | null) => {
      if (!tenantId) return;
      const append = Boolean(token);

      try {
        setLoading(true);
        setError(null);

        if (isPreviewMode()) {
          setRuns([]);
          setNextToken(null);
          return;
        }

        const client = generateClient();
        const response = (await client.graphql({
          query: LIST_FILES_MANAGER_RUNS,
          variables: { tenantId, limit: pageSize, nextToken: token ?? null },
        })) as any;

        const connection: FilesManagerRunConnection | undefined =
          response.data?.listFilesManagerRuns;
        if (connection) {
          setRuns((prev) => (append ? [...prev, ...connection.items] : connection.items));
          setNextToken(connection.nextToken);
        }
      } catch (err) {
        console.error('Failed to load Files manager runs:', err);
        setError("We couldn't load the runs for this tenant. Select Refresh to try again.");
      } finally {
        setLoading(false);
      }
    },
    [tenantId, pageSize]
  );

  const reset = useCallback(() => {
    setRuns([]);
    setNextToken(null);
    setError(null);
  }, []);

  return { runs, loading, error, nextToken, fetchRuns, reset };
}
